import React, { useEffect, useState } from "react";
import { Card, Form, Input, Button, List, Typography, Spin, Popconfirm, Space } from "antd";
import { getOrthodonticComments, createOrthodonticComment, deleteOrthodonticComment, updateOrthodonticComment } from "@/redux/actions/orthodonticCommentActions";
import { useSelector } from "react-redux";
import { _newLine, isValidDate } from "@/utils/helpers";
import HtmlParser from "react-html-parser";
import { ClockCircleOutlined, CloseOutlined, DeleteOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Text } = Typography;

const CommentForm = ({ planId }) => {

    const [form] = Form.useForm();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingContent, setEditingContent] = useState("");

    const currentUser = useSelector((s) => s.auth.authUser);

    // Load comments
    const loadComments = async () => {
        if (!planId) {
            setComments([]);
            return;
        }
        setLoading(true);
        try {
            const res = await getOrthodonticComments(planId)(() => { });
            // trick: vì action được viết dạng thunk, bạn có thể gọi bằng cách truyền dispatch = ()=>{}
            setComments(res.data || []);
        } catch (err) {
            setComments([]);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadComments();
    }, [planId]);

    // Submit thêm comment
    const handleSubmit = async (values) => {
        if (!planId) return;
        setLoading(true);
        try {
            await createOrthodonticComment(planId, { content: values.comment })(() => { });
            form.resetFields();
            await loadComments();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Xóa comment
    const handleDelete = async (commentId) => {
        if (!planId || !commentId) return;
        setLoading(true);
        try {
            await deleteOrthodonticComment(planId, commentId)(() => { });
            await loadComments();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Bắt đầu edit
    const startEdit = (item) => {
        setEditingId(item.id);
        setEditingContent(item.content);
    };

    // Hủy edit
    const cancelEdit = () => {
        setEditingId(null);
        setEditingContent("");
    };

    // Lưu edit
    const saveEdit = async (commentId) => {
        if (!editingContent.trim()) return;
        setLoading(true);
        try {
            await updateOrthodonticComment(planId, commentId, { content: editingContent })(() => { });
            setEditingId(null);
            setEditingContent("");
            await loadComments();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card
            title="COMMENTS"
            size="small"
            //style={{ position: "sticky", top: 60, zIndex: 100 }}
            bodyStyle={{ padding: 0 }}
            style={{ borderColor: "#108ee9" }}
            headStyle={{ backgroundColor: '#108ee9', color: '#fff' }}
        >
            <Spin spinning={loading}>
                {/* Danh sách comment */}
                <List
                    dataSource={comments}
                    itemLayout="vertical"
                    size="small"
                    locale={{ emptyText: "Chưa có bình luận" }}
                    renderItem={(item) => (
                        <Card size="small"
                            bodyStyle={{ padding: 8 }}
                            style={{ borderRadius: 0, borderBottom: "1px solid #f0f0f0" }}
                        >
                            <Text strong>
                                {item.created_user?.role_name && `${item.created_user.role_name} - `}
                                {item.created_user?.full_name || "Người dùng"}
                            </Text>
                            <div style={{ fontSize: 12, color: "#888"}}>
                                <ClockCircleOutlined /> {isValidDate(item.created_at)}
                            </div>
                            {/* Nếu đang edit */}
                            {editingId === item.id ? (
                                <div style={{ marginTop: 8 }}>
                                    <TextArea
                                        rows={5}
                                        value={_newLine(editingContent)}
                                        onChange={(e) => setEditingContent(e.target.value)}
                                    />
                                    <div style={{ textAlign: "right", marginTop: 8 }}>
                                        <Space>
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<SaveOutlined />}
                                                onClick={() => saveEdit(item.id)}
                                            >
                                                Lưu
                                            </Button>
                                            <Button
                                                size="small"
                                                icon={<CloseOutlined />}
                                                onClick={cancelEdit}
                                            >
                                                Hủy
                                            </Button>
                                        </Space>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            marginTop: 8,
                                            color: "#777",
                                            fontFamily:
                                                'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                        }}
                                    >
                                        {HtmlParser(item.content)}
                                    </div>

                                    {/* Action buttons */}
                                    {currentUser?.id === item.created_by && (
                                        <div style={{ textAlign: "right", marginTop: 8 }}>
                                            <Space>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => startEdit(item)}
                                                >
                                                    Sửa
                                                </Button>
                                                <Popconfirm
                                                    title="Bạn có chắc muốn xóa bình luận này?"
                                                    okText="Xóa"
                                                    cancelText="Hủy"
                                                    placement="left"
                                                    onConfirm={() => handleDelete(item.id)}
                                                >
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                    >
                                                        Xóa
                                                    </Button>
                                                </Popconfirm>
                                            </Space>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card>
                    )}
                />

                {/* Form thêm comment */}
                <Card size="small" style={{ marginTop: 12, borderRadius: 0 }}>
                    <Text>
                        Thêm comment
                        {currentUser?.role_name || currentUser?.full_name ? (
                            <>
                                {" của "}
                                <strong>
                                    {[currentUser?.role_name, currentUser?.full_name]
                                        .filter(Boolean)
                                        .join(" - ")}
                                </strong>
                            </>
                        ) : (
                            ""
                        )}
                    </Text>

                    <Form form={form} onFinish={handleSubmit} style={{ marginTop: 12 }}>
                        <Form.Item
                            name="comment"
                            rules={[{ required: true, message: "Vui lòng nhập comment" }]}
                        >
                            <TextArea rows={3} placeholder="Nhập nội dung comment..." />
                        </Form.Item>
                        <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
                            <Button type="primary" htmlType="submit" disabled={!planId}>
                                Submit
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </Spin>
        </Card>
    );
};

export default CommentForm;
